// The Inventory place's sums and words (M8), kept apart from the screen so they are tested.

export interface StockLocation {
  id: string;
  name: string;
  kind: 'warehouse' | 'truck';
  staffId: string | null;
  active: boolean;
}

export interface Level {
  locationId: string;
  itemId: string;
  itemName: string;
  onHand: string;
  min: string;
  max: string;
  countNeeded: boolean;
  flagReason: string | null;
}

export interface Cell {
  onHand: number;
  min: number;
  max: number;
  low: boolean;
  countNeeded: boolean;
}

export interface StockRow {
  itemId: string;
  itemName: string;
  cells: Record<string, Cell>;
  total: number;
}

/** "5.000" → 5, "2.500" → 2.5. */
export const qty = (s: string): number => Number(s);

/** A quantity as people write it: "5", "2.5". */
export function qtyText(n: number | string): string {
  const v = typeof n === 'string' ? Number(n) : n;
  return Number.isInteger(v) ? String(v) : String(Number(v.toFixed(3)));
}

/** The warehouse first, then the trucks, each by name. */
export function orderLocations(locations: readonly StockLocation[]): StockLocation[] {
  return [...locations]
    .filter((l) => l.active)
    .sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === 'warehouse' ? -1 : 1));
}

/** One row per item, a cell per location that stocks it; low when on hand is under the minimum. */
export function stockRows(levels: readonly Level[]): StockRow[] {
  const rows = new Map<string, StockRow>();
  for (const l of levels) {
    const row = rows.get(l.itemId) ?? { itemId: l.itemId, itemName: l.itemName, cells: {}, total: 0 };
    const cell: Cell = {
      onHand: qty(l.onHand),
      min: qty(l.min),
      max: qty(l.max),
      low: qty(l.min) > 0 && qty(l.onHand) < qty(l.min),
      countNeeded: l.countNeeded,
    };
    row.cells[l.locationId] = cell;
    row.total += cell.onHand;
    rows.set(l.itemId, row);
  }
  return [...rows.values()].sort((a, b) => a.itemName.localeCompare(b.itemName));
}

/**
 * Stock at one place (a technician's truck, opened from the board's tech menu): the rows that place
 * keeps, each with that place's cell alone, its total what is there.
 */
export function rowsAt(rows: readonly StockRow[], locationId: string): StockRow[] {
  return rows.flatMap((r) => {
    const c = r.cells[locationId];
    return c ? [{ ...r, cells: { [locationId]: c }, total: c.onHand }] : [];
  });
}

/** What one place's stock says with nothing there: "Nothing on Sam's van yet." */
export function nothingAtText(l: Pick<StockLocation, 'name' | 'kind'>): string {
  return `Nothing ${l.kind === 'truck' ? 'on' : 'in'} ${l.name} yet.`;
}

/** The screen's line for one place: "What is on Sam's van, against each item's minimum." */
export function oneLocationLede(l: Pick<StockLocation, 'name' | 'kind'>): string {
  return `What is ${l.kind === 'truck' ? 'on' : 'in'} ${l.name}, against each item’s minimum.`;
}

/** "2 low, 1 to count", or null when nothing needs anyone. */
export function attentionText(rows: readonly StockRow[]): string | null {
  let low = 0;
  let count = 0;
  for (const r of rows)
    for (const c of Object.values(r.cells)) {
      if (c.low) low++;
      if (c.countNeeded) count++;
    }
  const parts = [low ? `${low} low` : null, count ? `${count} to count` : null].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

/** Blueprint's facts over the table: the items stocked, the places under their minimum, those to count,
 * and those below zero (stock used that was never received: a count puts it right). */
export function stockFacts(rows: readonly StockRow[]): {
  items: number;
  low: number;
  toCount: number;
  belowZero: number;
} {
  const cells = rows.flatMap((r) => Object.values(r.cells));
  return {
    items: rows.length,
    low: cells.filter((c) => c.low).length,
    toCount: cells.filter((c) => c.countNeeded).length,
    belowZero: cells.filter((c) => c.onHand < 0).length,
  };
}

export const WHY_TEXT: Record<string, string> = {
  used_yesterday: 'Used yesterday',
  below_min: 'Below its minimum',
};

/** "Oct 15, 5:17 p.m.": when something happened, in the company's time zone. */
export function momentText(iso: string, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(iso));
}

export const PICKLIST_STATUS: Record<'open' | 'posted' | 'loaded', string> = {
  open: 'Not picked yet',
  posted: 'Picked',
  loaded: 'On the truck',
};

export const TRANSFER_REASON: Record<'picklist' | 'transfer' | 'return' | 'undo', string> = {
  picklist: 'Picklist',
  transfer: 'Transfer',
  return: 'Back to the warehouse',
  undo: 'Undo',
};

/** inventory.transfers' move (truck-stock parity 5: how much of each line was put back since). */
export interface Transfer {
  id: string;
  fromName: string;
  toName: string;
  reason: 'picklist' | 'transfer' | 'return' | 'undo';
  occurredAt: string;
  note: string | null;
  undoOf: string | null;
  lines: { itemId: string; itemName: string; quantity: string; undone: string }[];
}

/** What of a move can still be put back: each line less what undos put back. None for an undo. */
export function leftToUndo(t: Transfer): { itemId: string; itemName: string; left: number }[] {
  if (t.reason === 'undo') return [];
  return t.lines
    .map((l) => ({ itemId: l.itemId, itemName: l.itemName, left: Number(l.quantity) - Number(l.undone) }))
    .filter((l) => l.left > 0);
}

/** "4 × Ball valve", and "(1 put back)" or "(put back)" once undone. */
export function movedText(l: Transfer['lines'][number]): string {
  const base = `${qtyText(l.quantity)} × ${l.itemName}`;
  const back = Number(l.undone);
  if (!back) return base;
  return back >= Number(l.quantity) ? `${base} (put back)` : `${base} (${qtyText(back)} put back)`;
}

/** A quantity typed for a move: a positive number with at most three decimals. */
export function validQty(s: string): boolean {
  return /^\d{1,9}(\.\d{1,3})?$/.test(s.trim()) && Number(s) > 0;
}

/** Who moves stock and builds picklists (inventory.manage): the owner, the manager and the warehouse. */
export const STOCK_PERMS = ['inventory.manage'];

/** "box" → "boxes", "bag" → "bags": a vendor's unit, more than one. */
export function unitPlural(unit: string): string {
  return /(s|x|z|ch|sh)$/i.test(unit) ? `${unit}es` : `${unit}s`;
}

/**
 * A quantity bought by the vendor's unit, with what it is in single units (truck-stock parity 4):
 * "2 boxes (20 each)", "1.5 boxes (15 each)", or just "20" for an item bought one at a time.
 */
export function boxText(quantity: string, unitsPerPurchase: string, unit: string | null): string {
  if (!unit && Number(unitsPerPurchase) === 1) return qtyText(quantity);
  const n = Number(quantity);
  const name = unit ?? 'pack';
  return `${qtyText(quantity)} ${n === 1 ? name : unitPlural(name)} (${qtyText(Number((n * Number(unitsPerPurchase)).toFixed(3)))} each)`;
}
